import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, CreditCard, CheckCircle, AlertCircle, Clock,
  Receipt, Wallet, History, X, IndianRupee, BadgeCheck, Download
} from 'lucide-react';
import axios from 'axios';
import { printFeeReceipt } from '../lib/printFeeReceipt';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusIcon = (s) => {
  if (s === 'paid') return <CheckCircle className="h-4 w-4 text-emerald-500" />;
  if (s === 'overdue') return <AlertCircle className="h-4 w-4 text-red-500" />;
  if (s === 'partial') return <Clock className="h-4 w-4 text-blue-400" />;
  return <Clock className="h-4 w-4 text-amber-500" />;
};

const statusStyle = (s) => {
  if (s === 'paid') return 'text-emerald-700 bg-emerald-50 border-emerald-200';
  if (s === 'overdue') return 'text-red-700 bg-red-50 border-red-200';
  if (s === 'partial') return 'text-blue-700 bg-blue-50 border-blue-200';
  return 'text-amber-700 bg-amber-50 border-amber-200';
};

const METHODS = [
  { id: 'upi', label: 'UPI', icon: '📱' },
  { id: 'netbanking', label: 'Net Banking', icon: '🏦' },
  { id: 'card', label: 'Debit / Credit Card', icon: '💳' },
  { id: 'cash', label: 'Cash (at school)', icon: '💵' },
];

function PayModal({ invoice, onClose, onPaid }) {
  const token = localStorage.getItem('parent_token');
  const [method, setMethod] = useState('upi');
  const [paying, setPaying] = useState(false);
  const [success, setSuccess] = useState(null);
  const due = (invoice.net_amount || invoice.amount || 0) - (invoice.paid_amount || 0);

  const handlePay = async () => {
    setPaying(true);
    try {
      const res = await axios.post(
        `${API}/fees/pay`,
        { invoice_id: invoice.invoice_id, amount: due, payment_method: method },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(res.data.data);
      onPaid();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payment failed');
    } finally {
      setPaying(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl overflow-hidden">
        {success ? (
          /* ── Success screen ── */
          <div className="p-8 text-center">
            <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <BadgeCheck className="h-9 w-9 text-emerald-500" />
            </div>
            <h2 className="text-lg font-bold text-slate-900 mb-1">Payment Successful!</h2>
            <p className="text-slate-500 text-sm mb-4">Your payment has been recorded.</p>
            <div className="bg-slate-50 rounded-xl p-4 text-left space-y-2 text-sm mb-6">
              <div className="flex justify-between"><span className="text-slate-400">Amount Paid</span><span className="font-semibold text-emerald-600">₹{due.toLocaleString()}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Txn ID</span><span className="font-mono text-xs text-slate-600">{success.payment_id?.slice(0, 12).toUpperCase()}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Method</span><span className="capitalize text-slate-600">{method}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Status</span><span className="capitalize font-semibold text-slate-600">{success.invoice_status}</span></div>
            </div>
            <button onClick={onClose} className="w-full py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold transition-colors">Done</button>
          </div>
        ) : (
          /* ── Payment form ── */
          <>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <Wallet className="h-5 w-5 text-amber-500" />
                <h2 className="font-bold text-slate-900">Pay Fee</h2>
              </div>
              <button onClick={onClose}><X className="h-5 w-5 text-slate-400" /></button>
            </div>

            <div className="p-5 space-y-5">
              {/* Invoice summary */}
              <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
                <p className="text-xs text-amber-600 font-medium mb-1">{invoice.student_name} · {invoice.month}</p>
                <p className="text-2xl font-bold text-slate-900">₹{due.toLocaleString()}</p>
                <p className="text-xs text-slate-400 mt-0.5">{invoice.fee_type || 'School Fee'}</p>
                {invoice.paid_amount > 0 && (
                  <p className="text-xs text-emerald-600 mt-1">Already paid: ₹{invoice.paid_amount.toLocaleString()}</p>
                )}
              </div>

              {/* Payment method */}
              <div>
                <p className="text-xs font-semibold text-slate-500 mb-2">Select Payment Method</p>
                <div className="grid grid-cols-2 gap-2">
                  {METHODS.map(m => (
                    <button key={m.id} onClick={() => setMethod(m.id)}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-medium transition-colors ${method === m.id ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-slate-200 text-slate-600 hover:border-amber-300'}`}>
                      <span>{m.icon}</span> {m.label}
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={handlePay}
                disabled={paying}
                className="w-full py-3 rounded-xl bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white font-bold text-base transition-colors flex items-center justify-center gap-2"
              >
                {paying ? (
                  <><div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Processing...</>
                ) : (
                  <><IndianRupee className="h-4 w-4" /> Pay ₹{due.toLocaleString()}</>
                )}
              </button>
              <p className="text-center text-xs text-slate-400">This is a demo payment. No real transaction occurs.</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function ParentFees() {
  const navigate = useNavigate();
  const token = localStorage.getItem('parent_token');
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('dues');
  const [filter, setFilter] = useState('all');
  const [payInvoice, setPayInvoice] = useState(null);

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    fetchFees();
  }, []);

  const fetchFees = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/parent/fees`, { headers: { Authorization: `Bearer ${token}` } });
      setInvoices(res.data.data.invoices || []);
      setPayments(res.data.data.payments || []);
    } catch {
      toast.error('Failed to load fee details');
    } finally {
      setLoading(false);
    }
  };

  const unpaid = invoices.filter(i => i.status !== 'paid');
  const totalDue = unpaid.reduce((s, i) => s + ((i.net_amount || i.amount || 0) - (i.paid_amount || 0)), 0);
  const totalPaid = invoices.filter(i => i.status === 'paid').reduce((s, i) => s + (i.net_amount || i.amount || 0), 0);
  const overdue = invoices.filter(i => i.status === 'overdue').length;

  const filtered = filter === 'all' ? invoices : invoices.filter(i => i.status === filter);

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-4 sm:px-8 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Link to="/parent/dashboard" className="text-slate-400 hover:text-slate-700 transition-colors">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-amber-500" />
            <h1 className="font-bold text-slate-900 text-lg">Fee Management</h1>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-8 py-6 space-y-5">
        {loading ? (
          <div className="flex justify-center py-16"><div className="h-8 w-8 border-4 border-amber-200 border-t-amber-500 rounded-full animate-spin" /></div>
        ) : (
          <>
            {/* Summary */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-red-50 rounded-2xl p-4 text-center">
                <p className="text-xl font-bold text-red-700">₹{totalDue.toLocaleString()}</p>
                <p className="text-xs text-slate-500 mt-1">Total Due</p>
              </div>
              <div className="bg-emerald-50 rounded-2xl p-4 text-center">
                <p className="text-xl font-bold text-emerald-700">₹{totalPaid.toLocaleString()}</p>
                <p className="text-xs text-slate-500 mt-1">Total Paid</p>
              </div>
              <div className={`${overdue > 0 ? 'bg-orange-50' : 'bg-slate-50'} rounded-2xl p-4 text-center`}>
                <p className={`text-xl font-bold ${overdue > 0 ? 'text-orange-600' : 'text-slate-400'}`}>{overdue}</p>
                <p className="text-xs text-slate-500 mt-1">Overdue</p>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
              {[
                { id: 'dues', label: 'Fee Invoices', icon: Receipt },
                { id: 'history', label: 'Payment History', icon: History },
              ].map(t => (
                <button key={t.id} onClick={() => setTab(t.id)}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium transition-colors ${tab === t.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                  <t.icon className="h-4 w-4" /> {t.label}
                </button>
              ))}
            </div>

            {/* ── Fee Invoices Tab ── */}
            {tab === 'dues' && (
              <>
                <div className="flex gap-2 flex-wrap">
                  {['all', 'pending', 'overdue', 'partial', 'paid'].map(f => (
                    <button key={f} onClick={() => setFilter(f)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-colors ${filter === f ? 'bg-amber-500 text-white' : 'bg-white text-slate-500 border border-slate-200 hover:border-amber-300'}`}>
                      {f}
                    </button>
                  ))}
                </div>

                {filtered.length === 0 ? (
                  <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
                    <CreditCard className="h-10 w-10 text-slate-200 mx-auto mb-3" />
                    <p className="text-slate-400">No invoices found</p>
                  </div>
                ) : (
                  <div className="space-y-5">
                    {filtered.map((inv) => {
                      const due = (inv.net_amount || inv.amount || 0) - (inv.paid_amount || 0);
                      const canPay = inv.status !== 'paid' && due > 0;
                      return (
                        <div key={inv.invoice_id} className="bg-white rounded-2xl border border-slate-200 p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3">
                              {statusIcon(inv.status)}
                              <div>
                                <p className="text-sm font-semibold text-slate-800">{inv.student_name}</p>
                                <p className="text-xs text-slate-400">{inv.month} · {inv.is_additional ? inv.fee_type : 'School Fee'}</p>
                                {inv.due_date && <p className="text-xs text-slate-400 mt-0.5">Due: {fmtDate(inv.due_date)}</p>}
                                {inv.categories && inv.categories.length > 0 && (
                                  <div className="mt-1.5 flex flex-wrap gap-1">
                                    {inv.categories.map((cat, ci) => (
                                      <span key={ci} className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">
                                        {cat.name}: ₹{(cat.amount || 0).toLocaleString()}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="text-base font-bold text-slate-900">₹{(inv.net_amount || inv.amount || 0).toLocaleString()}</p>
                              {inv.paid_amount > 0 && (
                                <p className="text-xs text-emerald-600">Paid: ₹{inv.paid_amount.toLocaleString()}</p>
                              )}
                              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border capitalize mt-1 inline-block ${statusStyle(inv.status)}`}>
                                {inv.status}
                              </span>
                            </div>
                          </div>
                          <div className={`mt-3 flex gap-2 ${canPay ? '' : ''}`}>
                            {canPay && (
                              <button
                                onClick={() => setPayInvoice(inv)}
                                className="flex-1 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold transition-colors flex items-center justify-center gap-1.5"
                              >
                                <Wallet className="h-4 w-4" /> Pay Now — ₹{due.toLocaleString()}
                              </button>
                            )}
                            <button
                              onClick={() => {
                                const relatedPayment = payments.find(p => p.invoice_id === inv.invoice_id);
                                printFeeReceipt(inv, relatedPayment || null);
                              }}
                              className="py-2 px-3 rounded-xl border border-slate-200 hover:border-teal-400 hover:bg-teal-50 text-slate-500 hover:text-teal-600 text-sm font-medium transition-colors flex items-center gap-1.5"
                              title="Download PDF"
                            >
                              <Download className="h-4 w-4" />
                              {!canPay && <span>Download Receipt</span>}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {/* ── Payment History Tab ── */}
            {tab === 'history' && (
              payments.length === 0 ? (
                <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
                  <History className="h-10 w-10 text-slate-200 mx-auto mb-3" />
                  <p className="text-slate-400">No payment history yet</p>
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100 overflow-hidden">
                  {payments.map((p) => (
                    <div key={p.payment_id} className="flex items-center justify-between px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-xl bg-emerald-50 flex items-center justify-center">
                          <CheckCircle className="h-4 w-4 text-emerald-500" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-800 capitalize">{p.payment_method || 'Online'}</p>
                          <p className="text-xs text-slate-400 font-mono">{p.transaction_id || p.payment_id?.slice(0, 10)}</p>
                          <p className="text-xs text-slate-400">{fmtDate(p.paid_at)}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-base font-bold text-emerald-600">₹{(p.amount || 0).toLocaleString()}</p>
                        <span className="text-xs text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">Completed</span>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </>
        )}
      </main>

      {payInvoice && (
        <PayModal
          invoice={payInvoice}
          onClose={() => setPayInvoice(null)}
          onPaid={() => { setPayInvoice(null); fetchFees(); }}
        />
      )}
    </div>
  );
}
